import React, { useState } from "react";
import { Button, Spacer, SystemMessage } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { useCreditsQuery } from "../../graphql/generated/graphql";
import { useAppSettings } from "../../providers/appSettingsProvider";
import { Icon } from "../Icons";
import { ModalWrapper } from "../ModalWrapper";
import { SmallLoader } from "../SmallLoader";
import { CreditOptions, CreditTypes } from "./CreditOptions";
import { getRemainingCreditDays } from "./getRemainingCreditDays";
import { getCurrencyLimits, roundAmount, useCreditPayment } from "./useCreditPayment";

type CreditModalPropsT = {
  initialCustomAmount?: number;
  isOpen: boolean;
  onClose: () => void;
  useRecommendedMonthlyBudget?: boolean;
};

export const CreditModal = ({ initialCustomAmount, isOpen, onClose }: CreditModalPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.Credits" });
  const { currency: defaultCurrency } = useAppSettings();

  const [creditAmount, setCreditAmount] = useState<number>(0);
  const [isCreditValid, setIsCreditValid] = useState<boolean>(true);

  const currency = defaultCurrency;
  const { maximalPaymentAmount, minimalPaymentAmount } = getCurrencyLimits(currency);

  const { data, loading } = useCreditsQuery();
  const isFirstPayment = data?.organization?.boughtCredits?.amount === 0;
  const monthlyBudget = data?.organization?.recommendedCreditIncreaseSmall?.amount || 0;
  const monthlyBudgetThreeMonths = data?.organization?.recommendedCreditIncreaseBig?.amount || 0;
  const creditsBalance = data?.organization?.creditBalance?.amount || 0;
  const minimalNextPaymentAmount = isFirstPayment ? monthlyBudget : minimalPaymentAmount;
  const remainingOriginalDays = getRemainingCreditDays(data?.organization);

  const {
    error,
    initiatePaymentForAmount,
    isRedirecting,
    loading: loadingPayment,
  } = useCreditPayment({
    currency,
  });

  const topUpCredit = () => {
    initiatePaymentForAmount(creditAmount);
  };

  const handleCreditChange = (creditType: CreditTypes, amount: number, isValid: boolean) => {
    setCreditAmount(amount);
    setIsCreditValid(isValid);
  };

  const resetState = () => {
    setCreditAmount(monthlyBudgetThreeMonths);
    setIsCreditValid(true);
  };

  const isTopUpButtonDisabled = loadingPayment || !isCreditValid;

  if (loading || !data?.organization) {
    return <SmallLoader />;
  }

  return (
    <ModalWrapper isOpen={isOpen} title={t("Increase headline")} maxWidth onClose={onClose} onClosed={resetState}>
      {error ? (
        <div>
          <SystemMessage description={t("Unable to initiate payment")} level="error" />
          <Button onClick={onClose}>{t("Back")}</Button>
        </div>
      ) : isRedirecting ? (
        <div>
          <div style={{ justifyContent: "center", alignItems: "center", display: "flex", height: "300px" }}>
            <div style={{ textAlign: "center" }}>
              <Icon kind="loader" />
              <br />
              {t("Redirection to payment gaytway in progress")}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div>
            {isFirstPayment && (
              <div style={{ maxWidth: "500px", marginBottom: "12px" }}>{t("Optimal credit recharge info")}</div>
            )}
            {!isFirstPayment && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  gap: "8px",
                  padding: "16px",
                  border: "2px solid var(--app-primary)",
                  borderRadius: "var(--app-radius-box)",
                }}
              >
                <Icon kind="info-item" style={{ color: "var(--app-primary)" }} />
                <p style={{ margin: "0" }}>
                  <Trans
                    components={{ b: <b />, strong: <strong /> }}
                    t={t}
                    values={{ creditsBalance, days: remainingOriginalDays }}
                    i18nKey={
                      "The current status of your credit is {{ creditsBalance }}. Your credit will last for another {{ days }} days."
                    }
                  />
                </p>
              </div>
            )}
            <CreditOptions
              currency={currency}
              initialCreditType={initialCustomAmount ? CreditTypes.CUSTOM : undefined}
              isFirstPayment={isFirstPayment}
              maximalPaymentAmount={maximalPaymentAmount}
              minimalNextPaymentAmount={minimalNextPaymentAmount}
              monthlyBudget={monthlyBudget}
              monthlyBudgetThreeMonths={monthlyBudgetThreeMonths}
              initialCustomAmount={
                initialCustomAmount ? Math.max(roundAmount(initialCustomAmount), minimalNextPaymentAmount) : undefined
              }
              onCreditChange={handleCreditChange}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={onClose}>
              {t("Back")}
            </Button>
            <Spacer size="sm" />
            <Button
              data-test-id="credit-increase-save-button"
              disabled={isTopUpButtonDisabled}
              variant="primary"
              onClick={topUpCredit}
            >
              {t("Top up credit")}
            </Button>
          </div>
        </>
      )}
    </ModalWrapper>
  );
};
